/** Admin: overview, analytics, and reports (CSV exports for your CA, plus the audit log). */
import { api, saveDownload } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDateTime, inr } from '../../core/format.js';
import { ROLE_LABEL } from '../../core/constants.js';
import { barChart } from '../../ui/charts.js';
import { banner, ic, pageHead, seg, stat, table, tag } from '../../ui/components.js';
import { selectField } from '../../ui/forms.js';
import { toast } from '../../ui/toast.js';
import { navigate } from '../../core/router.js';
import { PAGE, pager, queryOf } from './shared.js';

const quiet = (p, fallback) => p.catch(() => fallback);

const overview = {
  path: '/admin/overview',
  roles: ['admin'],
  title: 'Overview',
  async load(ctx) {
    const [o, system] = await Promise.all([api.get('/admin/overview', { signal: ctx.signal }), quiet(api.get('/admin/system', { signal: ctx.signal }), null)]);
    return { o, system };
  },
  view({ o, system }) {
    const todo = [
      [o.pending_verifications, 'freelancer profiles waiting for verification', '/admin/users?role=professional&verification=pending', 'users'],
      [o.requirements_in_review, 'requirements to review', '/admin/requirements?status=in_review', 'clip'],
      [o.reports_open, 'safety reports open', '/admin/safety', 'shield'],
      [o.withdrawals_pending, 'withdrawals to pay', '/admin/payouts', 'wallet'],
      [o.deposits_due, 'deposits due for refund review', '/admin/deposits', 'rupee'],
    ].filter(([n]) => n > 0);
    const notReady = system ? system.checks.filter((c) => !c.ready) : [];
    return html`${pageHead('Platform overview', 'What needs you today.')}
      ${system && system.environment === 'production' && notReady.length ? banner(html`<b>Not ready for real customers:</b> ${notReady.map((c) => c.name).join(', ')}. See Settings.`, 'bad', 'alert') : ''}
      <div class="grid stats">${stat(inr(o.revenue_30d_paise), 'Revenue, last 30 days')}${stat(inr(o.recurring_revenue_paise), 'Recurring revenue / month')}${stat(o.active_subscriptions, 'Active subscriptions')}${stat(o.classes_today, `Classes today${o.classes_live_now ? `, ${o.classes_live_now} live` : ''}`)}</div>
      ${o.live_classes?.length ? html`<div class="card mt"><h3>Live now</h3><div class="list sm">${o.live_classes.map((c) => html`<div class="li">${ic('cam')}<span class="f1"><b>${c.course}</b> · Batch ${c.batch} · Class ${c.number}<br><span class="mu">Trainer: ${c.trainer || 'not set'}${c.trainer_in_room ? '' : ' (not in the room yet)'} · ${c.students_in_room} student${c.students_in_room === 1 ? '' : 's'} in the room</span></span>${tag('Live', 'ok')}</div>`)}</div></div>` : ''}
      <div class="grid g2 mt" style="align-items:start">
        <div class="card"><h3>Needs attention</h3>${todo.length
          ? html`<div class="list sm">${todo.map(([n, text, href, icon]) => html`<div class="li">${ic(icon)}<span class="f1"><b>${n}</b> ${text}</span><a class="btn sm" href="${href}">Open</a></div>`)}</div>`
          : html`<p class="mu" style="margin:0">All clear.</p>`}</div>
        <div class="card"><h3>People</h3><div class="list sm">${Object.entries(o.users_by_role).map(([role, n]) => html`<div class="li"><span class="f1">${ROLE_LABEL[role] || role}</span><b>${n}</b></div>`)}</div>
          <p class="note mt-s">Open requirements: ${o.requirements_open}. Lifetime subscription revenue: ${inr(o.revenue_total_paise)}.</p></div>
      </div>`;
  },
};

const analytics = {
  path: '/admin/analytics',
  roles: ['admin'],
  title: 'Analytics',
  ui: () => ({}),
  load: (ctx) => api.get('/admin/analytics', { signal: ctx.signal, query: { days: Number(ctx.query.get('days')) || 30 } }),
  view(d, _ui, ctx) {
    const days = Number(ctx.query.get('days')) || 30;
    return html`${pageHead('Analytics', 'Sign-ups and revenue by day, India time.', seg([['7', '7 days'], ['30', '30 days'], ['90', '90 days'], ['180', '180 days']], String(days), 'analytics.days', 'Range'))}
      <div class="grid g2" style="align-items:start"><div class="card">${barChart(d.labels, d.signups, { title: 'New accounts' })}</div>
        <div class="card">${barChart(d.labels, d.revenue_paise, { title: 'Revenue', format: (n) => inr(n) })}</div></div>`;
  },
};

register({ 'analytics.days': (el) => navigate(`/admin/analytics?days=${el.dataset.value}`) });

const DOWNLOADS = {
  users: ['/admin/export/users.csv', 'users.csv'],
  payments: ['/admin/export/payments.csv', 'payments.csv'],
  invoices: ['/admin/export/invoices.csv', 'invoices.csv'],
};

const reports = {
  path: '/admin/reports',
  roles: ['admin'],
  title: 'Reports',
  async load(ctx) {
    const query = queryOf(ctx);
    const [batches, audit] = await Promise.all([
      api.get('/admin/batches', { signal: ctx.signal }),
      api.get('/admin/audit', { signal: ctx.signal, query: { limit: PAGE, offset: query.offset || 0 } }),
    ]);
    return { batches: batches.batches, audit: audit.entries, query };
  },
  view(d) {
    return html`${pageHead('Reports and exports', 'Every export is recorded in the audit log.')}
      <div class="grid g2" style="align-items:start"><div class="card"><h3>Finance</h3><p class="mu sm">Hand the invoices file to your CA each month. It has the CGST, SGST and IGST split for every tax invoice.</p>
        <div class="row"><button type="button" class="btn" data-act="export.csv" data-key="invoices">${ic('dl')} Invoices (GST)</button><button type="button" class="btn ghost" data-act="export.csv" data-key="payments">${ic('dl')} Payments</button></div></div>
        <div class="card"><h3>People and attendance</h3><p class="mu sm">Contains personal data. Share only with people who need it.</p>
          <div class="row"><button type="button" class="btn ghost" data-act="export.csv" data-key="users">${ic('dl')} All users</button></div>
          <form class="mt-s" data-submit="export.attendance" novalidate>${selectField('ex-batch', 'Attendance for batch', d.batches.map((b) => [b.id, `Batch ${b.name}, course ${b.course_id}`]), { name: 'batch_id', placeholder: 'Choose a batch' })}<button class="btn ghost" type="submit">${ic('dl')} Attendance CSV</button></form></div></div>
      <h3 class="mt">Audit log</h3>${table(['When', 'Who', 'Action', 'Target', 'Details'], d.audit.map((a) => [fmtDateTime(a.at), a.actor_id ?? 'system', tag(a.action, 'n'), a.target, html`<span class="xs mu mono">${JSON.stringify(a.meta || {})}</span>`]), { empty: 'Nothing recorded yet.' })}
      ${pager('/admin/reports', d.query, d.audit.length)}`;
  },
};

register({
  'export.csv': async (el) => {
    const [path, name] = DOWNLOADS[el.dataset.key];
    await saveDownload(path, name);
    toast(`${name} downloaded`, 'dl');
  },
  'export.attendance': async (form) => {
    const batch = form.elements.batch_id.value;
    if (!batch) {
      toast('Choose a batch first.', 'alert');
      return;
    }
    await saveDownload('/admin/export/attendance.csv', `attendance-batch-${batch}.csv`, { query: { batch_id: batch } });
    toast('Attendance downloaded', 'dl');
  },
});

export const routes = [overview, analytics, reports];
